import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import io from "socket.io-client";
import DOMPurify from "dompurify"; // Import DOMPurify
import styles from "./AddEvent.module.css";
import { useAuth0 } from "@auth0/auth0-react";

const AddEvent = ({ isOpen, onClose }) => {
  const [title, setTitle] = useState(() => "");
  const [duration, setDuration] = useState(() => "");
  const [priority, setPriority] = useState(() => "");
  const [error, setError] = useState({
    title: "",
    duration: "",
    priority: "",
  });

  // Auth0 hook to get the user information
  const socket = useRef(null); // Using ref to hold the Socket.IO client instance
  const { user } = useAuth0();

  useEffect(() => {
    if (user && user.sub) {
      socket.current = io(process.env.REACT_APP_BACKEND_URL, {
        query: { userId: user.sub },
      });

      // Clean up the socket connection on component unmount
      return () => {
        if (socket.current) {
          socket.current.disconnect();
        }
      };
    }
  }, [user]);

  const handleTitleChange = (event) => {
    const { value } = event.target;
    setTitle(value); // Update the title state without sanitization

    if (value.length < 3) {
      setError((prevError) => ({
        ...prevError,
        title: "Title must be at least 3 characters long",
      }));
    } else {
      setError((prevError) => ({ ...prevError, title: "" }));
    }
  };

  const handleDurationChange = (event) => {
    const { value } = event.target;
    setDuration(value); // Update the duration state without sanitization

    const sanitizedValue = DOMPurify.sanitize(value); // Sanitize the input value for duration
    if (isNaN(sanitizedValue) || sanitizedValue <= 0) {
      setError((prevError) => ({
        ...prevError,
        duration: "Duration must be a positive number",
      }));
    } else {
      setError((prevError) => ({ ...prevError, duration: "" }));
    }
  };

  const handlePriorityChange = (event) => {
    const { value } = event.target;
    setPriority(value); // Update the priority state without sanitization

    const sanitizedValue = parseInt(DOMPurify.sanitize(value), 10); // Sanitize the input value for priority
    if (isNaN(sanitizedValue) || sanitizedValue < 1 || sanitizedValue > 5) {
      setError((prevError) => ({
        ...prevError,
        priority: "Priority must be a number between 1 and 5",
      }));
    } else {
      setError((prevError) => ({ ...prevError, priority: "" }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (title.trim() === "" || !duration || !priority) {
      setError({
        title: title.trim() === "" ? "Please enter a title." : "",
        duration: duration ? "" : "Please enter a duration.",
        priority: priority ? "" : "Please enter a priority.",
      });
    } else {
      setError({
        title: "",
        duration: "",
        priority: "",
      });

      const taskData = {
        userId: user.sub, // Add user's unique identifier from Auth0
        name: title,
        duration: parseInt(duration, 10),
        priority: parseInt(priority, 10),
      };

      try {
        socket.current.emit("createEvent", taskData); // Use socket from useRef

        // Clear the form fields and close the modal
        setTitle("");
        setDuration("");
        setPriority("");
        onClose();
      } catch (error) {
        console.error("Failed to add task:", error);
      }
    }
  };

  return (
    <Modal
      className={styles.modalContainer}
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        overlay: {
          zIndex: 1000,
        },
        content: {
          zIndex: 1001,
        },
      }}
    >
      <form className={styles.form} onSubmit={handleSubmit}>
        <input
          className={styles.label}
          placeholder="Title"
          type="text"
          value={title}
          onChange={handleTitleChange}
          onBlur={handleTitleChange} // Add onBlur event to trigger validation on focus loss
          required // Add required attribute
        />
        {error.title && <div className={styles.error}>{error.title}</div>}
        <div>
          <input
            className={styles.label}
            placeholder="Duration (in hours)"
            type="number"
            value={duration}
            onChange={handleDurationChange}
            onBlur={handleDurationChange} // Add onBlur event to trigger validation on focus loss
            required // Add required attribute
          />
          {error.duration && (
            <div className={styles.error}>{error.duration}</div>
          )}
        </div>
        <div>
          <input
            className={styles.label}
            placeholder="Priority (1 - Highest, 5 - Lowest)"
            type="number"
            value={priority}
            onChange={handlePriorityChange}
            onBlur={handlePriorityChange} // Add onBlur event to trigger validation on focus loss
            required // Add required attribute
          />
          {error.priority && (
            <div className={styles.error}>{error.priority}</div>
          )}
        </div>
        <button type="submit">Add Event</button>
      </form>
    </Modal>
  );
};

export default AddEvent;
